"use client";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { type RecipeVariantProps, css, cva, sva } from "@/../styled-system/css";

const sheet = sva({
  slots: ["overlay", "header", "footer", "title", "description"],
  base: {
    overlay: {
      position: "fixed",
      inset: "0",
      zIndex: "50",
      backgroundColor: "black/50",
      backdropFilter: "auto",
      backdropBlur: "sm",
      "&[data-state=open]": {
        animateIn: true,
        fadeIn: 0,
      },
      "&[data-state=closed]": {
        animateOut: true,
        fadeOut: 0,
      },
    },
    header: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
    },
    footer: {
      display: "flex",
      flexDirection: "column-reverse",
      sm: {
        flexDirection: "row",
        justifyContent: "flex-end",
        spaceX: "2",
      },
    },
    title: {
      textStyle: "2xl",
      fontWeight: "semibold",
      color: "black",
    },
    description: {
      textStyle: "sm",
      color: "black/70",
    },
  },
});

const { overlay, header, footer, title, description } = sheet.raw();

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(overlay, cssProp);

  return <SheetPrimitive.Overlay ref={ref} className={className} {...props} />;
});
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva({
  base: {
    position: "fixed",
    zIndex: "50",
    gap: "4",
    backgroundColor: "white",
    shadow: "lg",
    transition: "common",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    "&[data-state=open]": {
      animateIn: true,
      animationDuration: "500ms",
    },
    "&[data-state=closed]": {
      animateOut: true,
      animationDuration: "300ms",
    },
  },
  variants: {
    side: {
      top: {
        insetX: "0",
        top: "0",
        borderBottom: "base",
        "&[data-state=open]": {
          slideInFromTop: "100%",
        },
        "&[data-state=closed]": {
          slideOutToTop: "100%",
        },
      },
      bottom: {
        insetX: "0",
        bottom: "0",
        borderTop: "base",
        "&[data-state=open]": {
          slideInFromBottom: "100%",
        },
        "&[data-state=closed]": {
          slideOutToBottom: "100%",
        },
      },
      left: {
        insetY: "0",
        left: "0",
        h: "full",
        overflowY: "auto",
        "&[data-state=open]": {
          slideInFromLeft: "100%",
        },
        "&[data-state=closed]": {
          slideOutToLeft: "100%",
        },
      },
      right: {
        insetY: "0",
        right: "0",
        h: "full",
        overflowY: "auto",
        "&[data-state=open]": {
          slideInFromRight: "100%",
        },
        "&[data-state=closed]": {
          slideOutToRight: "100%",
        },
      },
    },
  },
  defaultVariants: {
    side: "right",
  },
});

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> &
    RecipeVariantProps<typeof sheetVariants> & { css?: object }
>(({ side = "right", children, css: cssProp = {}, ...props }, ref) => {
  const className = css(sheetVariants.raw({ side }), cssProp);

  return (
    <SheetPrimitive.Portal>
      <SheetOverlay />
      <SheetPrimitive.Content ref={ref} className={className} {...props}>
        {children}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { css?: object }) => {
  const className = css(header, cssProp);

  return <div className={className} {...props} />;
};
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { css?: object }) => {
  const className = css(footer, cssProp);

  return <div className={className} {...props} />;
};
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(title, cssProp);

  return <SheetPrimitive.Title ref={ref} className={className} {...props} />;
});
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(description, cssProp);

  return (
    <SheetPrimitive.Description ref={ref} className={className} {...props} />
  );
});
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
